import { useState, useContext, useEffect } from "react"
import { Field, FastField, useFormikContext } from "formik"
import { gql, useLazyQuery } from "@apollo/client"
import _ from "lodash"

import { Dropdown } from "primereact/dropdown"
import { FormContext } from "../../../context/formContext"
import { UserContext } from "../../../context/userContext"

import { LoaderSmall } from "../../../components/Loaders"

import styles from "../../../styles/Form.module.css"

export default function StaffIdSelector(props) {
  const { type, field, block, index, autoSelectStaff } = props

  const [staffList, setStaffList] = useState([])
  const [loading, setLoading] = useState(true)

  const userCtx = useContext(UserContext)

  const checkIfRequired = () => {
    try {
      return JSON.parse(field.field_validation.required[0])
    } catch (error) {
      return false
    }
  }

  const isRequired = checkIfRequired()
  const formik = useFormikContext()
  const form = useContext(FormContext)
  const user = useContext(UserContext)

  const GET_STAFF = gql`
    query getStaffList(
      $staff_id: ID!
      $organization_id: ID!
      $staff_membership_id: ID!
    ) {
      staffsByOrganization: staffMembersByOrganization(
        staff_id: $staff_id
        organization_id: $organization_id
      ) {
        staff_id
        staff_name_first
        staff_name_last
      }
      staffDataByStaffMembershipID(staff_membership_id: $staff_membership_id) {
        staff_id
        staff_name_first
        staff_name_last
      }
    }
  `

  const [getStaff] = useLazyQuery(GET_STAFF)

  useEffect(() => {
    const setOption = async () => {
      await getStaff({
        variables: {
          staff_id: userCtx.session.user.staff_id,
          organization_id: userCtx.session.user.organization_id,
          staff_membership_id: userCtx.current_staff_membership_id || "",
        },
        onCompleted: ({
          staffsByOrganization,
          staffDataByStaffMembershipID,
        }) => {
          const options = _.map(staffsByOrganization, (staff) => {
            const { staff_name_first, staff_name_last, staff_id } = staff
            return {
              label: `${_.unescape(staff_name_first)} ${_.unescape(
                staff_name_last
              )}`,
              key: staff_id,
            }
          })
  
          const staffInList = _.find(options, {
            key: staffDataByStaffMembershipID.staff_id,
          })
          if (!staffInList) {
            options.push({
              label: `${_.unescape(staffDataByStaffMembershipID.staff_name_first)} ${_.unescape(
                staffDataByStaffMembershipID.staff_name_last
              )}`,
              key: staffDataByStaffMembershipID.staff_id,
            })
          }
  
          const defaultOption = { label: "Unassigned Awaiting", key: "-4" }
          const staffs = [defaultOption].concat(options)
  
          const currentFormikField = formik.values[field.field_name] || null
          if (!currentFormikField) {
            const foundUser = _.find(staffs, {
              key: userCtx.session.user.staff_id,
            })
            let option = foundUser || defaultOption
            if (block === "document__document_record__staff") {
              option = defaultOption
            }
            if (autoSelectStaff) {
              option = _.find(staffs, {
                key: staffDataByStaffMembershipID.staff_id,
              })
            }
            formik.setFieldValue(field.field_name, option)
          } else {
            if (
              block === "participant__membership" &&
              form.idTemplate === "intake_reactivation_session"
            ) {
              formik.setFieldValue(field.field_name, {
                label: "Unassigned Awaiting",
                key: "-4",
              })
            }
          }
  
          setStaffList(staffs)
          setLoading(false)
        },
      })
    }
  
    setOption()
  
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  

  const isDisabled = form ? form.blocksSaved[block] : false

  const handleFieldPerType = () => {
    if (type === "multi-field") {
      const { field, mfFieldName, mfIndex } = props
      const fieldSlug = `${mfFieldName}.${mfIndex}.${field.field_name}`
      return (
        <FastField name={fieldSlug}>
          {({ field: formikField, meta }) => {
            const handleErr = () => {
              return typeof meta.error?.key === "string" ? (
                <div className={styles.errFieldMsg}>{meta.error.key}</div>
              ) : null
            }
            return (
              <>
                <Dropdown
                  placeholder="Please select"
                  id={field.field_name}
                  name={formikField.name}
                  value={formikField.value}
                  onChange={formikField.onChange}
                  options={staffList}
                  style={autoSelectStaff ? { display: "none" } : {}}
                />
                {handleErr()}
              </>
            )
          }}
        </FastField>
      )
    }
    return (
      <Field name={field.field_name}>
        {({ field: formikField, meta }) => (
          <>
            <Dropdown
              placeholder="Please select"
              id={field.field_name}
              name={formikField.name}
              value={formikField.value}
              onChange={formikField.onChange}
              disabled={isDisabled}
              options={staffList}
              style={autoSelectStaff ? { display: "none" } : {}}
            />
            {meta.touched && meta.error && (
              <div className={styles.errFieldMsg}>{meta.error}</div>
            )}
          </>
        )}
      </Field>
    )
  }

  if (loading) return <LoaderSmall />

  return (
    <div
      className={styles.field}
      style={{
        order: index,
      }}
    >
      {!autoSelectStaff && (
        <label className={styles.label}>
          {field.field_label}
          {isRequired && <span className={styles.asterisk}> *</span>}
        </label>
      )}
      {!autoSelectStaff && field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}

      {handleFieldPerType()}
    </div>
  )
}